import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivateFn} from '@angular/router';
import { Observable } from 'rxjs';
import {AuthenticationService} from './authentication.service';
import {map, switchMap, take} from 'rxjs/operators';
import {MediaService} from './media.service';
import {DeviceDetectorService} from 'ngx-device-detector';

@Injectable({
  providedIn: 'root'
})
class AuthenticationGuardService  {
  constructor(private authService: AuthenticationService, private router: Router, private mediaService: MediaService,
              private deviceService: DeviceDetectorService) { }
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // const path = route.routeConfig?.path;
    // console.log({path});
    const urlTree = this.router.parseUrl(state.url);
    urlTree.queryParams = {};
    const path = state.url.split('?')[0];
    const normalizedPath = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
    const isMobile = !this.deviceService.isDesktop() && !this.deviceService.isTablet();
    const isMobileMbRoute = normalizedPath.startsWith('/media-blast-mobile');
    const isMbRoute = normalizedPath.startsWith('/media-blast') && !isMobileMbRoute;
    const shouldKeepMbReturnUrl = isMobileMbRoute &&
      !['/media-blast-mobile', '/media-blast-mobile/login'].includes(normalizedPath);
    const mobileLoginRedirect = (): UrlTree => {
      if (shouldKeepMbReturnUrl) {
        return this.router.createUrlTree(['/media-blast-mobile/login'], { queryParams: { returnUrl: state.url }});
      }
      return this.router.createUrlTree(['/media-blast-mobile/login']);
    };
    const loginRedirect = (): UrlTree => {
      return this.router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url }});
    };
    const unauthRedirect = (): UrlTree => {
      return (isMobile && isMobileMbRoute) ? mobileLoginRedirect() : loginRedirect();
    };
    if (!this.mediaService.accessToken) {
      return unauthRedirect();
    }
    const decide = (): boolean | UrlTree => {
      const advertiserModules = this.mediaService.userAdvertiser?.modules || this.mediaService.selectedAdvertiser?.modules;
      const isMediaBlastUser = !this.mediaService.isAdminRole && !!advertiserModules?.mediablast;
      if (!isMobile) {
        if (isMobileMbRoute) {
          return this.router.parseUrl('/media-blast');
        }
        return true;
      }
      if (isMobileMbRoute) {
        return isMediaBlastUser ? true : this.router.parseUrl('/notDesktop');
      }
      if (isMbRoute) {
        return isMediaBlastUser ? this.router.parseUrl('/media-blast-mobile') : this.router.parseUrl('/notDesktop');
      }
      return this.router.parseUrl('/notDesktop');
    };
    const waitForAuthIfNeeded = (): Observable<boolean | UrlTree> => {
      if (isMobileMbRoute && this.mediaService.isAuthenticationInProcess
        && !this.mediaService.userAdvertiser && !this.mediaService.selectedAdvertiser) {
        return this.authService.onAuthenticationEnd.pipe(
          take(1),
          map(() => decide())
        );
      }
      return new Observable<boolean | UrlTree>(subscriber => {
        subscriber.next(decide());
        subscriber.complete();
      });
    };
    return this.authService.validate(urlTree.toString()).pipe(
      switchMap((valid) => {
        if (!valid) {
          return new Observable<boolean | UrlTree>(subscriber => {
            subscriber.next(unauthRedirect());
            subscriber.complete();
          });
        }
        return waitForAuthIfNeeded();
      })
    );
  }
}

export const AuthenticationGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot):
  Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  return inject(AuthenticationGuardService).canActivate(next, state);
};
